import '@apps/web/components/admin/llm_spend/llm_spend.css';

import { createFileRoute } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import { CircleDollarSign, FlaskConical, Info } from 'lucide-react';
import { Fragment, useCallback, useMemo } from 'react';
import { z } from 'zod';

import {
    AGENT_GENERATION_VALUES,
    type AgentGenerationSelection,
    DEFAULT_RANGE_PRESET,
    LLM_SPEND_EARLIEST_DATE,
    LLM_SPEND_TIMEZONE,
    presetDateRange,
    RANGE_PRESET_OPTIONS,
    RANGE_PRESET_VALUES,
    type RangePreset,
    rangePresetShortLabel,
    SPEND_MODE_VALUES,
    type SpendBucketSize,
    type SpendMode,
} from '@apps/web/components/admin/llm_spend/constants';
import { CustomersTab } from '@apps/web/components/admin/llm_spend/customers_tab';
import { OutreachTab } from '@apps/web/components/admin/llm_spend/outreach_tab';
import { OverviewTab } from '@apps/web/components/admin/llm_spend/overview_tab';
import {
    type NormalizedLlmSpendDashboardResponse,
    normalizeLlmSpendDashboardResponse,
} from '@apps/web/components/admin/llm_spend/response_compat';
import { buildSampleLlmSpendDashboard } from '@apps/web/components/admin/llm_spend/sample_data';
import { SchedulerTab } from '@apps/web/components/admin/llm_spend/scheduler_tab';
import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@apps/web/components/ui/alert';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    DateRangePicker,
    type DateRangePickerValue,
} from '@apps/web/components/ui/date_range_picker';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import { shiftDateKey } from '@apps/web/lib/date_utils';
import { useAdminLlmSpend } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const BUCKET_OPTIONS: ReadonlyArray<{
    readonly value: SpendBucketSize;
    readonly label: string;
}> = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
];

const MODE_OPTIONS: ReadonlyArray<{
    readonly value: SpendMode;
    readonly label: string;
}> = [
    { value: 'live', label: 'Live' },
    { value: 'sandbox', label: 'Sandbox' },
    { value: 'combined', label: 'Combined' },
];

const TAB_VALUES = ['overview', 'outreach', 'scheduler', 'customers'] as const;
type SpendTab = (typeof TAB_VALUES)[number];
const TAB_LABELS: Record<SpendTab, string> = {
    overview: 'Overview',
    outreach: 'Outreach agent',
    scheduler: 'Scheduler chat',
    customers: 'Orgs & users',
};

function isSpendTab(value: string): value is SpendTab {
    return (TAB_VALUES as ReadonlyArray<string>).includes(value);
}

export const Route = createFileRoute('/_app/admin/llm-spend')({
    component: LlmSpendPage,
    validateSearch: z.object({
        range: z.enum(RANGE_PRESET_VALUES).catch(DEFAULT_RANGE_PRESET),
        bucket: z.enum(['day', 'week']).catch('day'),
        mode: z.enum(SPEND_MODE_VALUES).catch('live'),
        tab: z.enum(TAB_VALUES).catch('overview'),
        generation: z.enum(AGENT_GENERATION_VALUES).catch('all'),
        // Absent means every organization; the filter applies to all tabs.
        org: z.array(z.string().uuid()).optional().catch(undefined),
        from: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional()
            .catch(undefined),
        to: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional()
            .catch(undefined),
        // Renders a deterministic fixture instead of the API so the page can
        // be reviewed before the nightly rollup produces real rows.
        sample: z.boolean().optional().catch(undefined),
    }),
    head: () => ({ meta: [{ title: 'LLM Spend | Phoebe Admin' }] }),
});

function LlmSpendPage() {
    const { range, bucket, mode, tab, generation, org, from, to, sample } =
        Route.useSearch();
    const navigate = Route.useNavigate();
    const customRange = useMemo<DateRangePickerValue | undefined>(
        () =>
            from !== undefined && to !== undefined ? { from, to } : undefined,
        [from, to],
    );
    const dateRange = useMemo(
        () => presetDateRange(range, new Date(), customRange),
        [customRange, range],
    );
    const isSample = sample === true;

    const {
        data: apiData,
        error,
        isLoading,
        mutate,
    } = useAdminLlmSpend(
        {
            range_start: dateRange.start,
            range_end: dateRange.end,
            bucket,
            mode,
            section: tab,
            agent_generation: generation,
            organization_ids: org,
            all_history: range === 'all' ? true : undefined,
        },
        { enabled: !isSample },
    );
    const sampleData = useMemo(
        () =>
            isSample
                ? buildSampleLlmSpendDashboard(
                      dateRange,
                      bucket,
                      mode,
                      generation,
                  )
                : null,
        [bucket, dateRange, generation, isSample, mode],
    );
    const data = useMemo(() => {
        const response = isSample ? sampleData : apiData;
        return response === undefined || response === null
            ? response
            : normalizeLlmSpendDashboardResponse(response);
    }, [apiData, isSample, sampleData]);

    const setSearch = useCallback(
        (
            next: Partial<{
                range: RangePreset;
                bucket: SpendBucketSize;
                mode: SpendMode;
                tab: SpendTab;
                generation: AgentGenerationSelection;
                org: Array<string> | undefined;
                from: string | undefined;
                to: string | undefined;
                sample: boolean | undefined;
            }>,
        ) => {
            void navigate({
                search: (prev) => ({ ...prev, ...next }),
                replace: true,
            });
        },
        [navigate],
    );
    const handleRetry = useCallback(() => {
        void mutate();
    }, [mutate]);
    const availableStart = useMemo(
        () =>
            data?.coverage.modes
                .flatMap((row) =>
                    row.history_start === null
                        ? []
                        : [row.history_start.slice(0, 10)],
                )
                .sort()[0] ?? LLM_SPEND_EARLIEST_DATE,
        [data?.coverage.modes],
    );
    const customPickerValue = customRange ?? {
        from: data?.range_start ?? dateRange.start,
        to: shiftDateKey(data?.range_end ?? dateRange.end, -1),
    };
    const lastCompleteDay = shiftDateKey(
        presetDateRange(DEFAULT_RANGE_PRESET).end,
        -1,
    );

    const handleRangeChange = useCallback(
        (next: RangePreset) => {
            if (next === 'custom') {
                setSearch({
                    range: next,
                    from: customPickerValue.from,
                    to: customPickerValue.to,
                });
                return;
            }
            setSearch({ range: next, from: undefined, to: undefined });
        },
        [customPickerValue.from, customPickerValue.to, setSearch],
    );

    return (
        <div className="llm-spend-root flex h-full flex-1 flex-col overflow-hidden">
            <header className="bg-muted/40 border-b">
                <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-3">
                    <div className="flex flex-col gap-1">
                        <h1 className="flex items-center gap-2 text-base font-semibold tracking-tight">
                            LLM Spend
                            {isSample ? (
                                <Badge variant="warning">Sample data</Badge>
                            ) : null}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Tracked token-priced estimates from the LLM cost
                            rollups. Buckets use UTC.
                        </p>
                    </div>
                    <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                        <div className="w-64">
                            <OrganizationCombobox
                                value={org ?? []}
                                onValueChange={(next) =>
                                    setSearch({
                                        org: next.length > 0 ? next : undefined,
                                    })
                                }
                                placeholder="All organizations"
                            />
                        </div>
                        <SegmentedControl
                            options={MODE_OPTIONS}
                            value={mode}
                            onValueChange={(next) => setSearch({ mode: next })}
                            size="sm"
                            layout="auto"
                            aria-label="Spend mode"
                        />
                        <Select
                            value={range}
                            onValueChange={(next) =>
                                handleRangeChange(next as RangePreset)
                            }
                        >
                            <SelectTrigger size="sm" className="w-44">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {RANGE_PRESET_OPTIONS.map((option) => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {range === 'custom' ? (
                            <DateRangePicker
                                value={customPickerValue}
                                timezone={LLM_SPEND_TIMEZONE}
                                showPresets={false}
                                minDateKey={availableStart}
                                maxDateKey={lastCompleteDay}
                                className="w-64"
                                onChange={(next) =>
                                    setSearch({
                                        range: 'custom',
                                        from: next.from,
                                        to: next.to,
                                    })
                                }
                            />
                        ) : null}
                        <SegmentedControl
                            options={BUCKET_OPTIONS}
                            value={bucket}
                            onValueChange={(next) =>
                                setSearch({ bucket: next })
                            }
                            size="sm"
                            layout="auto"
                            aria-label="Chart bucket size"
                        />
                        {data?.rollup_as_of ? (
                            <span className="tabular-nums">
                                rollup{' '}
                                {formatDistanceToNowStrict(
                                    new Date(data.rollup_as_of),
                                    { addSuffix: true },
                                )}
                            </span>
                        ) : null}
                        {isSample ? (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSearch({ sample: undefined })}
                            >
                                Exit sample
                            </Button>
                        ) : null}
                    </div>
                </div>
            </header>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
                <DashboardBody
                    data={data ?? undefined}
                    error={isSample ? undefined : error}
                    isLoading={isLoading && !isSample}
                    range={range}
                    bucket={bucket}
                    tab={tab}
                    generation={generation}
                    onTabChange={(next) => setSearch({ tab: next })}
                    onGenerationChange={(next) =>
                        setSearch({ generation: next })
                    }
                    onRetry={handleRetry}
                    onPreviewSample={() => setSearch({ sample: true })}
                />
            </div>
        </div>
    );
}

function DashboardBody({
    data,
    error,
    isLoading,
    range,
    bucket,
    tab,
    generation,
    onTabChange,
    onGenerationChange,
    onRetry,
    onPreviewSample,
}: {
    readonly data: NormalizedLlmSpendDashboardResponse | undefined;
    readonly error: Error | undefined;
    readonly isLoading: boolean;
    readonly range: RangePreset;
    readonly bucket: SpendBucketSize;
    readonly tab: SpendTab;
    readonly generation: AgentGenerationSelection;
    readonly onTabChange: (tab: SpendTab) => void;
    readonly onGenerationChange: (generation: AgentGenerationSelection) => void;
    readonly onRetry: () => void;
    readonly onPreviewSample: () => void;
}) {
    if (error !== undefined) {
        return (
            <AdminErrorState
                title="Could not load LLM spend"
                description={error.message}
                onRetry={onRetry}
            />
        );
    }
    if (data === undefined) {
        return isLoading ? <DashboardSkeleton /> : null;
    }
    if (data.rollup_as_of === null) {
        return (
            <AdminEmptyState
                icon={CircleDollarSign}
                title="No rollup data yet"
                description="The nightly LLM spend rollup has not produced rows for this range. Preview the page with sample data in the meantime."
                action={
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onPreviewSample}
                    >
                        <FlaskConical />
                        Preview with sample data
                    </Button>
                }
            />
        );
    }
    return (
        <Dashboard
            data={data}
            range={range}
            bucket={bucket}
            tab={tab}
            generation={generation}
            isLoading={isLoading}
            onTabChange={onTabChange}
            onGenerationChange={onGenerationChange}
        />
    );
}

function DashboardSkeleton() {
    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }, (_unused, index) => (
                    <Skeleton key={index} className="h-24" />
                ))}
            </div>
            <Skeleton className="h-80" />
            <div className="grid gap-4 xl:grid-cols-2">
                <Skeleton className="h-80" />
                <Skeleton className="h-80" />
            </div>
        </div>
    );
}

function CoverageNotice({
    data,
}: {
    readonly data: NormalizedLlmSpendDashboardResponse;
}) {
    const sources = formatSourceList(data.coverage.included_sources);
    // Every source here is priced by us, never billed by a provider, so the
    // amount stays “tracked estimated spend” in every rollout state.
    const complete = data.coverage.native_history_complete;
    const backfilling = complete === false;
    const detail = backfilling
        ? `Tracked estimated spend includes ${sources}. The newer sources` +
          ' are still reading their history, so earlier days understate' +
          ' them.'
        : `Tracked estimated spend includes ${sources}.`;
    return (
        <Alert>
            <Info />
            <AlertTitle>
                {backfilling ? 'Filling in history' : 'Partial spend coverage'}
            </AlertTitle>
            <AlertDescription>{detail}</AlertDescription>
        </Alert>
    );
}

function formatSourceList(sources: ReadonlyArray<string>): string {
    if (sources.length < 2) {
        return sources.join('');
    }
    const last = sources[sources.length - 1];
    return `${sources.slice(0, -1).join(', ')}, and ${last}`;
}

function Dashboard({
    data,
    range,
    bucket,
    tab,
    generation,
    isLoading,
    onTabChange,
    onGenerationChange,
}: {
    readonly data: NormalizedLlmSpendDashboardResponse;
    readonly range: RangePreset;
    readonly bucket: SpendBucketSize;
    readonly tab: SpendTab;
    readonly generation: AgentGenerationSelection;
    // True while a tab, generation, or range change is fetching; the
    // previous response stays in `data` until then.
    readonly isLoading: boolean;
    readonly onTabChange: (tab: SpendTab) => void;
    readonly onGenerationChange: (generation: AgentGenerationSelection) => void;
}) {
    const comparisonLabel =
        range === 'all' ? null : `vs prior ${rangePresetShortLabel(range)}`;
    const handleTabChange = useCallback(
        (next: string) => {
            if (isSpendTab(next)) {
                onTabChange(next);
            }
        },
        [onTabChange],
    );

    return (
        <div className="flex flex-col gap-4">
            <CoverageNotice data={data} />
            <Tabs value={tab} onValueChange={handleTabChange}>
                <TabsList>
                    {TAB_VALUES.map((value) => (
                        <TabsTrigger key={value} value={value}>
                            {TAB_LABELS[value]}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
            {isLoading ? (
                <DashboardSkeleton />
            ) : (
                <Fragment>
                    {tab === 'overview' ? (
                        <OverviewTab
                            data={data}
                            bucket={bucket}
                            comparisonLabel={comparisonLabel}
                        />
                    ) : null}
                    {tab === 'outreach' ? (
                        <OutreachTab
                            data={data}
                            bucket={bucket}
                            generation={generation}
                            onGenerationChange={onGenerationChange}
                        />
                    ) : null}
                    {tab === 'scheduler' ? (
                        <SchedulerTab
                            data={data}
                            bucket={bucket}
                            generation={generation}
                            onGenerationChange={onGenerationChange}
                        />
                    ) : null}
                    {tab === 'customers' ? <CustomersTab data={data} /> : null}
                </Fragment>
            )}
        </div>
    );
}

export default LlmSpendPage;
